// List editor for ParamField type "list" (posterTools/types.ts).
//
// Object lists (itemSchema): collapsed rows show a one-line summary built
// from summaryKeys (first key = row title); clicking a row opens a modal
// (same cce-modal-overlay / cce-modal pattern as SaveDesignModal in
// StudioCoreEditor.jsx) rendering the full itemSchema for that row.
//
// Scalar lists (itemType: text | number | color): the control renders inline
// in the row — no modal.
//
// Every row gets − (remove) and ▲▼ (reorder); + appends an item derived from
// the schema's sensible empties. All edits buffer into the panel draft via
// onChange — Apply regenerates, same as every other field.
import React, { useState } from 'react';
import SmartObjectFieldControl from './SmartObjectFieldControl';
// Scalar color rows use the house PaintField (owner 2026-07-23: EVERY color
// selector is the uniform picker) — solid-only + opaque, '#rrggbb' out,
// byte-identical to the retired <input type=color> (str() consumers).
import { PaintField, solidHexFrom } from './primitives/PaintPicker';
import { defaultListItem, isFieldVisible, listRowSummary, safeHexInput } from './SmartObjectFieldUtils';

export default function SmartObjectListField({ field, value, onChange }) {
  const [openIndex, setOpenIndex] = useState(null); // object-list modal row
  const items = Array.isArray(value) ? value : [];
  const isScalar = !!field.itemType;
  const atMax = Number.isFinite(field.maxItems) && items.length >= field.maxItems;
  const atMin = Number.isFinite(field.minItems) && items.length <= field.minItems;

  const setItems = (next) => onChange(next);

  const addItem = () => {
    if (atMax) return;
    setItems([...items, defaultListItem(field)]);
    if (!isScalar) setOpenIndex(items.length); // jump straight into the new row
  };

  const removeItem = (i) => {
    if (atMin) return;
    setItems(items.filter((_, idx) => idx !== i));
    if (openIndex !== null) setOpenIndex(null);
  };

  const moveItem = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };

  const patchItem = (i, itemValue) => {
    const next = [...items];
    next[i] = itemValue;
    setItems(next);
  };

  // ARRAY items (numeric-key itemSchemas, e.g. data-table rows) must stay
  // arrays — an object spread would turn ['a','b'] into {0:'a',1:'b'} and the
  // recipe would drop the row on regenerate.
  const patchItemKey = (i, key, v) => {
    const item = items[i];
    const next = Array.isArray(item) ? [...item] : { ...item };
    next[key] = v;
    patchItem(i, next);
  };

  const renderScalarControl = (item, i) => {
    if (field.itemType === 'number') {
      // Slider + compact number box (owner 2026-07-23: EVERY number input gets a
      // slider). Same range law as the scalar 'number' control: field.min/max/step
      // when present, else a live-adaptive upper bound of max(2×value, 100) so the
      // track keeps headroom. The box stays the truth (may exceed the slider);
      // both emit the same string patchItem already expects.
      const curNum = Number.parseFloat(item);
      const hasNum = Number.isFinite(curNum);
      const rMin = Number.isFinite(field.min) ? field.min : 0;
      const rMax = Number.isFinite(field.max)
        ? field.max
        : Math.max((hasNum ? curNum : 0) * 2, 100);
      const rStep = Number.isFinite(field.step) ? field.step : ((rMax - rMin) <= 5 ? 0.1 : 1);
      const sliderVal = hasNum ? Math.min(rMax, Math.max(rMin, curNum)) : rMin;
      return (
        <div className="cce-so-slider-row cce-so-row-slider">
          <input
            type="range"
            className="cce-so-range"
            min={rMin}
            max={rMax}
            step={rStep}
            value={sliderVal}
            aria-label={`${field.label} ${i + 1}`}
            onChange={(e) => patchItem(i, e.target.value)}
          />
          <input
            type="number"
            className="cce-so-input cce-so-row-input cce-so-slider-num"
            value={item ?? ''}
            onChange={(e) => patchItem(i, e.target.value)}
          />
        </div>
      );
    }
    if (field.itemType === 'color') {
      const hex = typeof item === 'string' ? item : solidHexFrom(item, '#cccccc');
      return (
        <div className="cce-so-paint cce-so-row-paint">
          <PaintField
            label={`${field.label} ${i + 1}`}
            dataField={`${field.key}-${i}`}
            value={safeHexInput(hex, '#cccccc')}
            allow={['solid']}
            opaque
            chipClassName="cce-so-paint-chip"
            onChange={(next) => patchItem(i, typeof next === 'string' ? next : solidHexFrom(next, '#cccccc'))}
          />
          <span className="cce-so-paint-sum">{safeHexInput(hex, '#cccccc')}</span>
        </div>
      );
    }
    return (
      <input
        type="text"
        className="cce-so-input cce-so-row-input"
        value={item ?? ''}
        onChange={(e) => patchItem(i, e.target.value)}
      />
    );
  };

  const modalOpen = !isScalar && openIndex !== null && openIndex < items.length;
  const openItem = modalOpen ? items[openIndex] : null;
  const openSummary = modalOpen ? listRowSummary(field, openItem, openIndex) : null;

  return (
    <div className="cce-so-listfield">
      <div className="cce-so-list-rows">
        {items.map((item, i) => {
          const { title, subtitle } = listRowSummary(field, item, i);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="cce-so-list-row">
              {isScalar ? (
                renderScalarControl(item, i)
              ) : (
                <button
                  type="button"
                  className="cce-so-list-summary"
                  title="Edit this item"
                  onClick={() => setOpenIndex(i)}
                >
                  <span className="cce-so-list-title">{title}</span>
                  {subtitle && <span className="cce-so-list-sub">{subtitle}</span>}
                </button>
              )}
              <div className="cce-so-list-rowbtns">
                <button
                  type="button"
                  className="cce-so-rowbtn"
                  title="Move up"
                  aria-label="Move up"
                  disabled={i === 0}
                  onClick={() => moveItem(i, -1)}
                >
                  ▲
                </button>
                <button
                  type="button"
                  className="cce-so-rowbtn"
                  title="Move down"
                  aria-label="Move down"
                  disabled={i === items.length - 1}
                  onClick={() => moveItem(i, 1)}
                >
                  ▼
                </button>
                <button
                  type="button"
                  className="cce-so-rowbtn cce-so-rowbtn-remove"
                  title="Remove"
                  aria-label="Remove"
                  disabled={atMin}
                  onClick={() => removeItem(i)}
                >
                  −
                </button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="cce-so-list-empty">No items yet — add one below.</div>
        )}
      </div>

      <button
        type="button"
        className="cce-so-list-add"
        disabled={atMax}
        onClick={addItem}
      >
        + Add item
      </button>

      {modalOpen && (
        <div
          className="cce-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setOpenIndex(null)}
        >
          <div className="cce-modal cce-so-item-modal">
            <h2 className="cce-modal-title cce-so-item-modal-title">
              {field.label} — {openSummary.title}
            </h2>
            <div className="cce-so-item-modal-body">
              {/* Conditional visibility (visibleWhen, SM-F2): a sub-field's
                  condition is evaluated against THIS row's own values, so a
                  per-item controller hides its dependents inside the row modal
                  exactly like the top-level form. */}
              {(field.itemSchema || []).filter((sub) => isFieldVisible(sub, openItem)).map((sub) => (
                <div key={sub.key} className="cce-so-field">
                  <label className="cce-so-label">{sub.label}</label>
                  {sub.type === 'list' ? (
                    // Nested lists are not expected in itemSchemas; recurse
                    // anyway so nothing renders as raw JSON.
                    <SmartObjectListField
                      field={sub}
                      value={openItem?.[sub.key]}
                      onChange={(v) => patchItemKey(openIndex, sub.key, v)}
                    />
                  ) : (
                    <SmartObjectFieldControl
                      field={sub}
                      value={openItem?.[sub.key]}
                      onChange={(v) => patchItemKey(openIndex, sub.key, v)}
                    />
                  )}
                  {sub.help && <p className="cce-so-help">{sub.help}</p>}
                </div>
              ))}
            </div>
            <div className="cce-so-item-modal-actions">
              <button
                type="button"
                className="cce-so-apply-btn cce-so-item-done"
                onClick={() => setOpenIndex(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
